import { useToast } from 'native-base';
import { useHeapContext } from '../../../../contexts/heapContext';
import { usePortalContext } from '../../../../contexts/portalContext';
import { useTimeoutContext } from '../../../../contexts/timeoutContext';
import { useLogoutHubMutation } from '../../../../graphQL';
import { removeMobileToken, removeRedirectUrl } from '../../../../utils/authenticationStore';
import { useDisableDevice } from './useDisableDevice';

type LogoutResponse = [() => Promise<void>];

type LogoutHookOptions = {
  onCompleted: (output: boolean) => void;
};

export function useLogout({ onCompleted }: LogoutHookOptions): LogoutResponse {
  const toast = useToast();
  const { clearTokenExpiry } = useTimeoutContext();
  const { setIsHeapUserRegistered } = useHeapContext();
  const { asyncDisableDevice } = useDisableDevice();
  const { removeBlock } = usePortalContext();

  const [logoutHubMutation] = useLogoutHubMutation({
    onCompleted: async data => {
      const loggedOutRes = data.logoutHub;
      if (loggedOutRes) {
        setIsHeapUserRegistered(false);
      }

      // Make sure to remove blocks and refresh the portals to clear out previous portal access.
      await removeBlock({ isLogout: true });

      onCompleted(loggedOutRes);
    },
    onError: error => {
      // If the user has already been logged out, we don't need to show an error.
      if (!error.message.includes('Invalid Authentication')) {
        toast.show({
          description: 'Could not log out',
        });
      }
    },
  });

  const logoutMutation = async (): Promise<void> => {
    clearTokenExpiry();

    await asyncDisableDevice();
    await logoutHubMutation();
    await removeRedirectUrl();
    await removeMobileToken();
  };

  return [logoutMutation];
}
